import { track } from '@/lib/analytics/track';
import { TrackingEventName } from '@/models/analytics/eventTypes';
import {
  MOBILE_APP_DOWNLOAD_SCREEN,
  type MobileAppDownloadScreenToken,
  type TrackMobileAppDownloadModalViewed,
} from '@heidi/growth/contracts';
import { ScreenName } from '@heidi/runtime/analytics-vocabulary';

/**
 * The app-side emit host for the "Get Heidi on mobile" modal (`@heidi/growth`).
 *
 * Ruling 24: the modal decides WHEN the event fires — its open-state effect,
 * which reports once per open — and supplies the payload; this adds the
 * `event_name`, the destination routing, and nothing else.
 *
 * ROUTING IS PRESERVED, NOT TIDIED: the call site passed `{ amplitude: false }`
 * and that is reproduced exactly.
 */

/**
 * The package's `screen_name` token bound to the app enum member that declares
 * it.
 *
 * `modal_viewed` is one of the app's cross-domain generic events — forty call
 * sites across nine domains — so its name is not the growth domain's to declare
 * (D9 ruling 27) and the `ScreenName` vocabulary stays app-side. This map is the
 * one place the package's token meets it, so the strong `TrackingEventV2` union
 * check still runs over a payload the domain built.
 * `trackMobileAppDownloadAnalytics.test.ts` asserts the entry maps the token to
 * an enum member of the identical value, which is what makes this a binding
 * rather than a second spelling.
 *
 * `satisfies Record<Token, ScreenName>` makes it TOTAL by construction: a token
 * added to the package with no enum member fails here, at the binding, rather
 * than at the first emit.
 */
const SCREEN_NAME_BY_TOKEN = {
  [MOBILE_APP_DOWNLOAD_SCREEN.getHeidiMobile]: ScreenName.GetHeidiMobile,
} as const satisfies Record<MobileAppDownloadScreenToken, ScreenName>;

/** The download modal became visible. */
export const trackMobileAppDownloadModalViewed: TrackMobileAppDownloadModalViewed = ({
  screen_name,
}) => {
  track({
    options: { amplitude: false },
    event_name: TrackingEventName.ModalViewed,
    props: { screen_name: SCREEN_NAME_BY_TOKEN[screen_name] },
  });
};
